import { useMutation } from "@apollo/client";
import { FormEvent, useState } from "react";
import {
  CreateOrUpdateOrganizationSettingsMutation,
  CreateOrUpdateOrganizationSettingsMutationVariables,
  OrgSettingsOrganizationFragmentFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import AiKnowledgeEditor from "@components/ai-knowledge-editor/ai-knowledge-editor";
import Button from "@components/button/button";
import Input from "@components/input/input";

import updateOrganizationMutation from "../../../org-settings/graphql/update-organization-mutation";

type Props = {
  organization: OrgSettingsOrganizationFragmentFragment;
};

const OrgAiBlock = ({ organization }: Props) => {
  const label = useLabel();
  const [anthropicApiKey, setAnthropicApiKey] = useState("");
  const [isSavingAnthropicApiKey, setIsSavingAnthropicApiKey] = useState(false);
  const [updateOrganization] = useMutation<
    CreateOrUpdateOrganizationSettingsMutation,
    CreateOrUpdateOrganizationSettingsMutationVariables
  >(updateOrganizationMutation);

  const handleSaveAiKnowledge = async (jsonValue: string) => {
    if (organization.aiGuidelines === jsonValue) return;
    await updateOrganization({
      variables: {
        organizationId: organization.id,
        aiGuidelines: jsonValue,
      },
      optimisticResponse: {
        createOrUpdateOrganization: {
          organization: {
            ...organization,
            aiGuidelines: jsonValue,
          },
          __typename: "CreateOrUpdateOrganizationMutation",
        },
        __typename: "Mutation",
      },
    });
  };

  const handleSaveAnthropicApiKey = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedApiKey = anthropicApiKey.trim();
    if (!trimmedApiKey) return;

    setIsSavingAnthropicApiKey(true);
    try {
      await updateOrganization({
        variables: {
          organizationId: organization.id,
          anthropicApiKey: trimmedApiKey,
        },
        optimisticResponse: {
          createOrUpdateOrganization: {
            organization: {
              ...organization,
              hasAnthropicApiKey: true,
            },
            __typename: "CreateOrUpdateOrganizationMutation",
          },
          __typename: "Mutation",
        },
      });
      setAnthropicApiKey("");
      successNotificationVar({ title: "Anthropic API key saved" });
    } catch (error) {
      errorNotificationVar({
        title:
          error instanceof Error
            ? error.message
            : "Could not save Anthropic API key",
      });
    } finally {
      setIsSavingAnthropicApiKey(false);
    }
  };

  const handleClearAnthropicApiKey = async () => {
    setIsSavingAnthropicApiKey(true);
    try {
      await updateOrganization({
        variables: {
          organizationId: organization.id,
          anthropicApiKey: "",
        },
        optimisticResponse: {
          createOrUpdateOrganization: {
            organization: {
              ...organization,
              hasAnthropicApiKey: false,
            },
            __typename: "CreateOrUpdateOrganizationMutation",
          },
          __typename: "Mutation",
        },
      });
      setAnthropicApiKey("");
      successNotificationVar({ title: "Anthropic API key removed" });
    } catch (error) {
      errorNotificationVar({
        title:
          error instanceof Error
            ? error.message
            : "Could not remove Anthropic API key",
      });
    } finally {
      setIsSavingAnthropicApiKey(false);
    }
  };

  return (
    <>
      <div className="pb-8">
        <h3 className="text-base font-medium text-gray-800 mb-1">
          Anthropic API key
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {organization.hasAnthropicApiKey
            ? "A workspace Anthropic API key is configured."
            : "Add a workspace Anthropic API key to run AI requests through Anthropic."}
        </p>
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={handleSaveAnthropicApiKey}
        >
          <Input
            type="password"
            label="API key"
            value={anthropicApiKey}
            onChange={(event) => setAnthropicApiKey(event.target.value)}
            autocomplete="off"
            placeholder={
              organization.hasAnthropicApiKey
                ? "Enter a new key to replace the current key"
                : "sk-ant-..."
            }
            className="sm:w-96"
            aria-label="Anthropic API key"
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              text="Save"
              theme="primary"
              disabled={!anthropicApiKey.trim() || isSavingAnthropicApiKey}
            />
            {organization.hasAnthropicApiKey && (
              <Button
                text="Remove"
                theme="default"
                disabled={isSavingAnthropicApiKey}
                onClick={handleClearAnthropicApiKey}
              />
            )}
          </div>
        </form>
      </div>

      <div className="pt-8 border-t">
        <h3 className="text-base font-medium text-gray-800 mb-1">
          AI knowledge
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {label("organization", { capitalize: true })}-wide context like
          acronyms, internal processes, and response preferences so Topicflow AI
          can tailor answers.
        </p>
        <AiKnowledgeEditor
          value={organization.aiGuidelines || null}
          onSave={handleSaveAiKnowledge}
          label="AI Knowledge"
          description={`Add ${label(
            "organization",
          )}-specific context like company acronyms, internal processes, and response preferences so Topicflow AI can tailor answers.`}
          placeholder="e.g. Prioritize goal data, explain company acronyms, and reference internal processes."
        />
      </div>
    </>
  );
};

export default OrgAiBlock;
